/**
 * @file schemas.ts
 * @brief Zod schemas + TS types for the Super Admin → Sessions feature.
 *
 * @details
 * Mirrors the response shapes produced by `BACKEND/src/features/admin/`
 * (the additive routes added on 26-05-2026):
 * - `service.js::getAllSessions`     → `{ sessions, pagination }`
 * - `service.js::getSessionByIdAdmin` → single session row + `utterances[]`
 *
 * Money is stored in **minor units** (`total_cents`) — exactly the same
 * convention used elsewhere in the panel. Forms / displays convert to
 * major units via `formatMoneyMinor` at the UI boundary.
 *
 * `passthrough()` is used liberally so the panel survives backend column
 * additions without breaking parsing on every deploy.
 *
 * @references
 * - BACKEND/src/features/admin/service.js (getAllSessions, getSessionByIdAdmin)
 * - BACKEND/src/features/admin/routes.js  (route shapes)
 * - BACKEND/src/db/schema.sql              (sessions, utterances tables)
 *
 * @author DES
 * @date 26-05-2026
 */

import { z } from "zod";

// ----------------------------------------------------------------------
// Status / kind enums
// `status` mirrors `sessions.status` and `kind` mirrors `session_kind`.
// `.catch` keeps the badge rendering even if the backend introduces a
// new value before the panel ships an update.
// ----------------------------------------------------------------------
export const SessionStatusSchema = z
  .enum(["in_progress", "completed", "failed", "cancelled", "deleted"])
  .catch("completed");
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

export const SessionKindSchema = z
  .enum(["standard", "realtime"])
  .catch("standard");
export type SessionKind = z.infer<typeof SessionKindSchema>;

// ----------------------------------------------------------------------
// Numeric coercion — Postgres COUNT/SUM come back as strings.
// ----------------------------------------------------------------------
const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

// ----------------------------------------------------------------------
// Admin session row (list endpoint)
// ----------------------------------------------------------------------
export const AdminSessionSchema = z
  .object({
    session_id: z.string(),
    started_at: z.string().nullable().optional(),
    ended_at: z.string().nullable().optional(),
    duration_seconds: numericString.optional().default(0),
    source_language: z.string().nullable().optional(),
    target_language: z.string().nullable().optional(),
    status: SessionStatusSchema,
    currency: z.string().nullable().optional(),
    total_cents: numericString.nullable().optional(),
    person_a_name: z.string().nullable().optional(),
    person_b_name: z.string().nullable().optional(),
    /* Avatar / gender — Person A maps to the session owner's profile
       image (already signed by the backend). Person B avatar is not
       persisted today, so the panel falls back to gender-aware /
       initials placeholders. */
    person_a_avatar_url: z.string().nullable().optional(),
    person_a_gender: z.string().nullable().optional(),
    person_b_gender: z.string().nullable().optional(),
    reference_number: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    notes: z.string().nullable().optional(),
    session_kind: SessionKindSchema,

    /* Owner join */
    owner_id: z.string().nullable().optional(),
    owner_first_name: z.string().nullable().optional(),
    owner_last_name: z.string().nullable().optional(),
    owner_email: z.string().nullable().optional(),

    /* Company join */
    company_id: z.string().nullable().optional(),
    company_name: z.string().nullable().optional(),
  })
  .passthrough();

export type AdminSession = z.infer<typeof AdminSessionSchema>;

// ----------------------------------------------------------------------
// Utterance row (detail endpoint)
// ----------------------------------------------------------------------
export const AdminUtteranceSchema = z
  .object({
    speaker: z.string().nullable().optional(),
    original_text: z.string().nullable().optional(),
    translated_text: z.string().nullable().optional(),
    timestamp_ms: numericString.nullable().optional(),
    utterance_index: numericString.nullable().optional(),
    /* Per-utterance audio — backend re-signs every fetch (1h TTL).
       `src_audio_url` is the original capture, `tgt_audio_url` is the
       translated TTS output. Either may be null when the asset wasn't
       persisted (e.g. early realtime sessions). */
    src_audio_url: z.string().nullable().optional(),
    tgt_audio_url: z.string().nullable().optional(),
  })
  .passthrough();

export type AdminUtterance = z.infer<typeof AdminUtteranceSchema>;

// ----------------------------------------------------------------------
// Detail row — same as list row plus `transcript_url`, `invoice_url`,
// and `utterances[]`. Detail endpoint also returns the same flat join
// columns so we extend rather than reshape.
// ----------------------------------------------------------------------
export const AdminSessionDetailSchema = AdminSessionSchema.extend({
  transcript_url: z.string().nullable().optional(),
  invoice_url: z.string().nullable().optional(),
  /* Full-conversation audio (mixed, single file). Signed for 1h. */
  audio_url: z.string().nullable().optional(),
  utterances: z.array(AdminUtteranceSchema).default([]),
  /* Super Admin detail (B2/W4, 22-10-2026): counts instead of content. */
  utterance_count: numericString.nullable().optional(),
  has_audio: z.boolean().nullable().optional(),
  has_transcript: z.boolean().nullable().optional(),
  content_hidden: z.boolean().nullable().optional(),
  /* Billing detail for the Summary (mobile cost sheet parity, 30-09-2026).
     Sent by `/api/sessions/:id` (company scope); the admin route may omit
     them, and the panel then hides what it can't state. */
  billing_context: z.string().nullable().optional(),
  rate_per_min_cents: numericString.nullable().optional(),
  min_charge_cents: numericString.nullable().optional(),
  session_charge: numericString.nullable().optional(),
  is_paid: z.boolean().nullable().optional(),
  /* Money status (BACKEND moneyStatus.mjs, 18-10-2026): billed vs collected,
     refunds, failure, deleted, dispute. Absent on older backends. */
  payment_status: z.string().nullable().optional(),
  billed_cents: numericString.nullable().optional(),
  captured_cents: numericString.nullable().optional(),
  refunded_cents: numericString.nullable().optional(),
  payment_failed: z.boolean().nullable().optional(),
  is_deleted: z.boolean().nullable().optional(),
  dispute_status: z.string().nullable().optional(),
});

export type AdminSessionDetail = z.infer<typeof AdminSessionDetailSchema>;

// ----------------------------------------------------------------------
// Pagination + list envelope
// ----------------------------------------------------------------------
export const AdminPaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

export const AdminSessionsListResponseSchema = z.object({
  sessions: z.array(AdminSessionSchema),
  pagination: AdminPaginationSchema,
});

export type AdminSessionsListResponse = z.infer<
  typeof AdminSessionsListResponseSchema
>;

// ----------------------------------------------------------------------
// List query params (sent to GET /api/admin/sessions)
// Keep snake_case names that mirror the backend route param parser so
// the URL → params → request transform is loss-less.
// ----------------------------------------------------------------------
export interface AdminSessionsListParams {
  page?: number;
  limit?: number;
  status?: SessionStatus;
  kind?: SessionKind;
  company_id?: string;
  user_id?: string;
  source_language?: string;
  target_language?: string;
  from?: string;
  to?: string;
  search?: string;
  include_deleted?: boolean;
}

export function sessionOwnerLabel(row: AdminSession): string {
  const ownerName = [row.owner_first_name, row.owner_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (ownerName) return ownerName;
  if (row.owner_email) return row.owner_email;
  return "—";
}

export function sessionOwnerKey(row: AdminSession): string {
  if (row.owner_id?.trim()) return row.owner_id.trim();
  if (row.owner_email?.trim()) return row.owner_email.trim().toLowerCase();
  return sessionOwnerLabel(row).toLowerCase();
}
